import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatMoney, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FABRICACION, EMPAQUE, ENTREGA, FACTURACION, classFor, labelFor } from "@/lib/statuses";
import { BarChart3, ClipboardList, ExternalLink, Calendar } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArticulosPorUsuario } from "@/components/ArticulosVendidos";
import { Package, Receipt, Hourglass } from "lucide-react";
import { agruparSemanas, totalesArticulos, SemanaHeader } from "@/components/SemanasVentas";

function Stat({ label, value, sub, icon: Icon, accent = "bg-[#002FA7]" }) {
  return (
    <Card className="p-5 border-zinc-200 rounded-md">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold">{label}</div>
          <div className="mt-2 font-manrope font-black tracking-tight text-3xl">{value}</div>
          {sub && <div className="mt-1 text-xs text-zinc-500">{sub}</div>}
        </div>
        <div className={`w-9 h-9 rounded-md grid place-items-center ${accent}`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
    </Card>
  );
}

function toISO(d) { return d.toISOString().slice(0, 10); }
function startOfMonth() { const d = new Date(); d.setDate(1); return d; }
function daysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d; }

const PRESETS = [
  { key: "hoy",   label: "Hoy",             from: () => new Date(),           to: () => new Date() },
  { key: "7d",    label: "Últimos 7 días",  from: () => daysAgo(6),           to: () => new Date() },
  { key: "mes",   label: "Este mes",        from: () => startOfMonth(),       to: () => new Date() },
  { key: "30d",   label: "Últimos 30 días", from: () => daysAgo(29),          to: () => new Date() },
  { key: "todo",  label: "Todo",            from: () => null,                  to: () => null },
];

export default function MisVentas() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [activePreset, setActivePreset] = useState("todo");
  const [users, setUsers] = useState([]);
  // Admin: '' = mis propias ventas, 'all' = todas, 'email' = de ese vendedor
  const [vendorFilter, setVendorFilter] = useState("");
  const [modo, setModo] = useState("actual"); // actual | historial
  const [cierres, setCierres] = useState([]);
  const [cierreId, setCierreId] = useState("");
  const [semCierres, setSemCierres] = useState([]);
  const [abiertas, setAbiertas] = useState({});
  const scope = isAdmin ? (vendorFilter === "" ? user?.email : vendorFilter === "all" ? "general" : vendorFilter) : user?.email;
  const loadSem = useCallback(() => {
    api.get("/mis-ventas/cierres-semanales", { params: isAdmin ? { vendedor: scope } : {} }).then(({ data }) => setSemCierres(data || [])).catch(() => {});
  }, [isAdmin, scope]);
  useEffect(() => { loadSem(); }, [loadSem]);

  useEffect(() => {
    api.get("/cierres-mes").then(({ data }) => setCierres(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (isAdmin) {
      if (vendorFilter === "" || !vendorFilter) params.mine = true;
      else if (vendorFilter === "all") { /* no filter, admin ve todos */ }
      else params.vendedor = vendorFilter;
    } else {
      params.mine = true;
    }
    if (modo === "historial") {
      if (cierreId) params.cierre_id = cierreId;
      else params.historial = true;
    } else {
      params.incluir_cerrados = true;
    }
    api.get("/orders", { params })
      .then(({ data }) => {
        const items = Array.isArray(data) ? data : (data.items || data);
        setOrders(items || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [vendorFilter, isAdmin, modo, cierreId]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/users").then(({ data }) => setUsers(data.filter((u) => u.role === "vendedor" || u.role === "admin"))).catch(() => {});
  }, [isAdmin]);

  const applyPreset = (p) => {
    const f = p.from();
    const t = p.to();
    setFrom(f ? toISO(f) : "");
    setTo(t ? toISO(t) : "");
    setActivePreset(p.key);
  };

  const inRange = (iso) => {
    if (!iso) return true;
    const d = iso.slice(0, 10);
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  };

  const filtered = useMemo(() => orders.filter((o) => inRange(o.fecha)), [orders, from, to]);

  const total = filtered.length;
  const enProceso = filtered.filter((o) => !["pagado", "cancelado"].includes(o.estado)).length;
  const art = totalesArticulos(filtered);
  const semanas = agruparSemanas(filtered, from || to ? [] : semCierres);
  const cierreDe = (ini) => semCierres.find((c) => c.inicio === ini && !c.reabierto && (c.vendedor === scope || c.vendedor === "general"));

  const rangeLabel = () => {
    if (!from && !to) return "Todo el histórico";
    if (from && to) return `${formatDate(from)} → ${formatDate(to)}`;
    if (from) return `Desde ${formatDate(from)}`;
    return `Hasta ${formatDate(to)}`;
  };

  return (
    <div className="space-y-6" data-testid="mis-ventas-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Panel del vendedor</div>
        <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
          <BarChart3 className="w-8 h-8 text-[#002FA7]" /> Mis Ventas
        </h1>
        <p className="text-zinc-500 mt-2 text-sm">Hola {user?.name?.split(" ")[0]}, aquí está el estado de {isAdmin && vendorFilter && vendorFilter !== "" ? (vendorFilter === "all" ? "TODAS las ventas" : `las ventas de ${users.find((u) => u.email === vendorFilter)?.name || vendorFilter}`) : "todos tus pedidos"}.</p>
      </header>

      {/* Admin-only vendor selector */}
      {isAdmin && (
        <Card className="p-3 border-blue-200 border-2 bg-blue-50/50 rounded-md" data-testid="admin-vendor-selector">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="text-[10px] uppercase tracking-widest text-blue-800 font-bold">🔒 Vista Admin</div>
            <Label className="text-xs">Ver ventas de:</Label>
            <Select value={vendorFilter || "mine"} onValueChange={(v) => setVendorFilter(v === "mine" ? "" : v)}>
              <SelectTrigger className="w-64" data-testid="admin-vendor-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mine">Mis propias ventas (admin)</SelectItem>
                <SelectItem value="all">Todas las ventas (TODOS los usuarios)</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.email} value={u.email}>{u.name} — {u.role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>
      )}

      {/* Date range filter */}
      <Card className="p-4 border-zinc-200 rounded-md">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Rango de fechas
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {PRESETS.map((p) => (
                <Button
                  key={p.key}
                  size="sm"
                  variant={activePreset === p.key ? "default" : "outline"}
                  className={`h-7 text-xs ${activePreset === p.key ? "bg-[#002FA7] hover:bg-[#00227A]" : ""}`}
                  onClick={() => applyPreset(p)}
                  data-testid={`preset-${p.key}`}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Desde</Label>
              <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setActivePreset(""); }} data-testid="range-from" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Hasta</Label>
              <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setActivePreset(""); }} data-testid="range-to" />
            </div>
          </div>
        </div>
        <div className="mt-3 text-xs text-zinc-500">
          Mostrando <span className="font-semibold text-zinc-900">{filtered.length}</span> pedido{filtered.length === 1 ? "" : "s"} · <span className="font-semibold">{rangeLabel()}</span>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div data-testid="stat-pedidos"><Stat label="Pedidos" value={art.pedidos} sub={`${enProceso} activos`} icon={ClipboardList} /></div>
        <div data-testid="stat-articulos-vendidos"><Stat label="Artículos vendidos" value={art.articulos} sub={`${art.pedidos} pedido${art.pedidos === 1 ? "" : "s"} vigentes`} icon={Package} accent="bg-[#7C3AED]" /></div>
        <div data-testid="stat-articulos-facturados"><Stat label="Artículos facturados" value={art.facturados} sub="pedidos con número de factura" icon={Receipt} accent="bg-emerald-600" /></div>
        <div data-testid="stat-articulos-pendientes"><Stat label="Pendientes de facturar" value={art.pendientes} sub="artículos sin factura" icon={Hourglass} accent="bg-[#F59E0B]" /></div>
      </div>

      {isAdmin && vendorFilter === "all" && <ArticulosPorUsuario orders={filtered} users={users} />}

      <Card className="border-zinc-200 rounded-md overflow-hidden">
        <div className="p-5 border-b border-zinc-200">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Detalle</div>
          <div className="text-xl font-semibold mt-0.5">Todos mis pedidos con estado por área</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr className="text-left">
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Pedido</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Fecha entrega</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Cliente</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Fabricación</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Empaque</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Entrega</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Facturación</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold text-right">A cobrar</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {loading && (
                <tr><td colSpan={9} className="text-center text-zinc-400 py-8">Cargando...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="text-center text-zinc-400 py-12">
                  <ClipboardList className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
                  No hay pedidos en este rango.
                </td></tr>
              )}
              {semanas.map((s) => {
                const cierre = cierreDe(s.ini);
                const abierta = abiertas[s.ini] ?? !cierre;
                return [
                  <SemanaHeader key={`h-${s.ini}`} s={s} cierre={cierre} scope={scope} isAdmin={isAdmin} abierta={abierta} colSpan={9}
                    onToggle={() => setAbiertas({ ...abiertas, [s.ini]: !abierta })} onChanged={loadSem} />,
                  ...(abierta ? s.orders : []).map((o) => {
                const aCobrar = o.a_cobrar != null ? o.a_cobrar : (o.total - (o.anticipo || 0));
                return (
                  <tr key={o.id} className="hover:bg-zinc-50 align-top">
                    <td className="px-4 py-3">
                      <div className="font-mono text-xs font-semibold">{o.numero}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">{formatDate(o.fecha)}</div>
                      <div className="mt-1">
                        <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                          o.envio_tipo === "ciudad" ? "bg-blue-100 text-blue-900" : "bg-violet-100 text-violet-900"
                        }`}>
                          {o.envio_tipo === "ciudad" ? "Ciudad" : "Departamento"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {o.fecha_entrega ? (
                        <span className="text-sm font-semibold text-orange-700">{formatDate(o.fecha_entrega)}</span>
                      ) : <span className="text-zinc-300 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{o.cliente_nombre}</div>
                      <div className="text-xs text-zinc-500 truncate max-w-[180px]">{o.direccion}</div>
                    </td>
                    {[
                      { list: FABRICACION, val: o.estado_fabricacion || "pendiente" },
                      { list: EMPAQUE,    val: o.estado_empaque || "pendiente" },
                      { list: ENTREGA,    val: o.estado_entrega || "pendiente" },
                      { list: FACTURACION, val: o.estado_facturacion || "pendiente_facturar" },
                    ].map((c, i) => (
                      <td key={i} className="px-4 py-3">
                        <span className={`inline-block rounded-full ${classFor(c.list, c.val)} px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider`}>
                          {labelFor(c.list, c.val)}
                        </span>
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right tabular-nums">
                      <div className="font-semibold text-emerald-700">{formatMoney(aCobrar)}</div>
                      {o.anticipo ? <div className="text-[10px] text-zinc-500">Anticipo {formatMoney(o.anticipo)}</div> : null}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/pedidos/${o.id}`} className="text-[#002FA7] text-xs font-semibold uppercase tracking-widest inline-flex items-center gap-1">
                        Abrir <ExternalLink className="w-3 h-3" />
                      </Link>
                    </td>
                  </tr>
                );
              })];
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
